import { useEffect, useId, useState, type CSSProperties } from 'react'
import type { ServerApiClient, ServerSite } from '../../data/ServerApiClient'
import { FgHelp } from '../help/FgHelp'

/** Roles an invite can give, in the order the server's `normalize_site_role` names them. */
const INVITE_ROLES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'explorer_can_publish', label: 'Explorer (can publish)' },
  { value: 'creator', label: 'Creator' },
  { value: 'site_admin_explorer', label: 'Site admin (explorer)' },
  { value: 'site_admin_creator', label: 'Site admin (creator)' },
]

type Props = {
  api: Pick<ServerApiClient, 'listAdminSites' | 'inviteAdminUser'>
  onClose: () => void
  onInvited: () => void | Promise<void>
}

const fieldStyle: CSSProperties = { display: 'grid', gap: 6, border: 0, padding: 0, margin: 0 }
const labelStyle: CSSProperties = { fontSize: 13, fontWeight: 700, color: '#1c1f26', display: 'flex', alignItems: 'center', gap: 6 }
const buttonStyle: CSSProperties = { border: '1px solid #e7e3d8', borderRadius: 8, background: '#fff', padding: '8px 12px', fontWeight: 600, color: '#1d4c64', cursor: 'pointer' }

/**
 * Invite a person to the tenant, choosing the sites they get. The picker lists only the sites the
 * inviter administers (`GET /api/admin/sites`: sites of tenants where they are a tenant admin), and
 * the server refuses any other site.
 */
export function TenantUserInviteDialog({ api, onClose, onInvited }: Props) {
  const id = useId().replace(/[^A-Za-z0-9_-]/g, '')
  const [sites, setSites] = useState<ServerSite[] | null>(null)
  const [loadError, setLoadError] = useState('')
  const [email, setEmail] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const [role, setRole] = useState('viewer')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    api.listAdminSites().then(
      (rows) => { if (!cancelled) setSites(rows) },
      (err) => { if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Could not load your sites') },
    )
    return () => { cancelled = true }
  }, [api])

  const toggle = (siteId: string) =>
    setSelected((current) => current.includes(siteId) ? current.filter((value) => value !== siteId) : [...current, siteId])
  const canSubmit = !busy && email.trim() !== '' && selected.length > 0

  const submit = async () => {
    if (!canSubmit) return
    setBusy(true)
    setError('')
    try {
      await api.inviteAdminUser({
        email: email.trim(),
        memberships: selected.map((site_id) => ({ site_id, site_role: role, status: 'active' })),
      })
      await onInvited()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite user')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,31,38,0.35)', display: 'grid', placeItems: 'center', zIndex: 50 }}>
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        onSubmit={(event) => { event.preventDefault(); void submit() }}
        style={{ background: 'var(--fg-surface, #fff)', borderRadius: 12, padding: 20, width: 'min(480px, calc(100vw - 32px))', display: 'grid', gap: 16 }}
      >
        <h2 id={`${id}-title`} style={{ margin: 0, fontSize: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
          Invite a user<FgHelp id="users.invite" />
        </h2>
        <div style={fieldStyle}>
          <label htmlFor={`${id}-email`} style={labelStyle}>Email<FgHelp id="users.invite_email" /></label>
          <input
            id={`${id}-email`}
            type="email"
            autoComplete="off"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            style={{ border: '1px solid #ddd', borderRadius: 6, padding: 8 }}
          />
        </div>
        <fieldset style={fieldStyle}>
          <legend style={labelStyle}>Sites<FgHelp id="users.invite_sites" /></legend>
          {loadError ? <div role="alert">{loadError}</div>
            : sites === null ? <div>Loading your sites…</div>
              : sites.length === 0 ? <div>You don&apos;t administer any sites yet.</div>
                : sites.map((site) => (
                  <label key={site.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                    <input type="checkbox" checked={selected.includes(site.id)} onChange={() => toggle(site.id)} />
                    {site.name}
                  </label>
                ))}
        </fieldset>
        <div style={fieldStyle}>
          <label htmlFor={`${id}-role`} style={labelStyle}>Role on the chosen sites<FgHelp id="users.invite_role" /></label>
          <select id={`${id}-role`} value={role} onChange={(event) => setRole(event.target.value)} style={{ border: '1px solid #ddd', borderRadius: 6, padding: 8 }}>
            {INVITE_ROLES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </div>
        {error ? <div role="alert" style={{ color: '#b91c1c', fontSize: 13 }}>{error}</div> : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" style={buttonStyle} onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" style={{ ...buttonStyle, background: '#1d4c64', color: '#fff' }} disabled={!canSubmit}>
            {busy ? 'Inviting…' : 'Send invite'}
          </button>
        </div>
      </form>
    </div>
  )
}
